import { useQuery } from "@tanstack/react-query";
import { Navigate, Outlet } from "react-router-dom";
import { api, ApiError } from "../lib/api.js";
import type { UserProfile } from "@mnp/shared";

/**
 * Гейт корневых маршрутов: грузит /profile и решает, куда пускать клиента.
 *
 * Контракт редиректов (план 2026-06-12 §4.14, карточка appgate-error-retry-screen):
 *   - /welcome — ТОЛЬКО при 404 (профиля нет) или успешном ответе
 *     с profileComplete=false (профиль не дозаполнен);
 *   - 401 → «Откройте приложение из Telegram» (нет/невалиден initData);
 *   - любая другая ошибка (500, сетевой TypeError, 429) → экран
 *     «Повторить» БЕЗ Navigate. Иначе существующий клиент при сбое API
 *     выглядит «разлогиненным» и попадает в онбординг заново.
 */

/**
 * Ретраим только не-4xx сбои (5xx, сетевой TypeError): до 2 авто-повторов
 * с экспоненциальным backoff. 4xx (включая 429 после rate-limit-шторма)
 * детерминированы или требуют паузы — для них ручная кнопка «Повторить».
 */
export function shouldRetryProfile(failureCount: number, err: unknown): boolean {
  if (err instanceof ApiError && err.status >= 400 && err.status < 500) return false;
  return failureCount < 2;
}

export function AppGate() {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["profile"],
    queryFn: () => api<UserProfile & { stats?: { totalOrders: number } }>("/profile"),
    retry: shouldRetryProfile,
    retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 5000),
  });

  if (isLoading) {
    return (
      <div className="app-shell" style={{ alignItems: "center", justifyContent: "center" }}>
        <p className="t-body">Загрузка…</p>
      </div>
    );
  }

  if (error instanceof ApiError && error.status === 404) {
    return <Navigate to="/welcome" replace />;
  }

  if (error instanceof ApiError && error.status === 401) {
    return (
      <div className="app-shell" style={{ padding: 24, textAlign: "center" }}>
        <p className="t-body">Откройте приложение из Telegram</p>
      </div>
    );
  }

  if (error) {
    // 500 / 429 / сеть: профиль, скорее всего, существует — в онбординг не выкидываем.
    return (
      <div
        className="app-shell"
        style={{
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          textAlign: "center",
          gap: 12,
        }}
      >
        <p className="t-body">Не удалось загрузить данные. Проверьте соединение</p>
        <button
          className="btn btn-primary"
          onClick={() => void refetch()}
          disabled={isFetching}
        >
          Повторить
        </button>
      </div>
    );
  }

  if (!data?.profileComplete) {
    return <Navigate to="/welcome" replace />;
  }

  return <Outlet />;
}
